import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Package,
  DollarSign,
  AlertTriangle,
  Warehouse,
  TrendingUp,
  TrendingDown,
  ArrowRight,
} from "lucide-react";
import { dashboardApi } from "../lib/api";
import { toast } from "sonner";

const MetricCard = ({ title, value, icon: Icon, trend, trendValue, color = "blue" }) => {
  const colorClasses = {
    blue: "text-blue-400 bg-blue-500/10",
    green: "text-emerald-400 bg-emerald-500/10",
    red: "text-red-400 bg-red-500/10",
    yellow: "text-amber-400 bg-amber-500/10",
  };

  return (
    <div className="metric-card card-hover" data-testid={`metric-${title.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-start justify-between mb-4">
        <div className={`p-2 rounded-sm ${colorClasses[color]}`}>
          <Icon className="w-5 h-5" />
        </div>
        {trend && (
          <div className={`flex items-center gap-1 text-xs ${trend === "up" ? "text-emerald-400" : "text-red-400"}`}>
            {trend === "up" ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
            {trendValue}
          </div>
        )}
      </div>
      <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">{title}</p>
      <p className="text-2xl font-bold text-white font-mono">{value}</p>
    </div>
  );
};

const AlertItem = ({ alert }) => (
  <div 
    className="flex items-center justify-between py-3 border-b border-slate-800 last:border-0"
    data-testid={`alert-${alert.sku}`}
  >
    <div className="flex items-center gap-3">
      <div className={`status-dot ${alert.severity === "critical" ? "status-dot-error" : "status-dot-warning"}`} />
      <div>
        <p className="text-sm font-medium text-slate-200">{alert.name}</p>
        <p className="text-xs text-slate-500 font-mono">{alert.sku}</p>
      </div>
    </div>
    <div className="text-right">
      <p className={`text-sm font-mono ${alert.severity === "critical" ? "text-red-400" : "text-amber-400"}`}>
        {alert.quantity} / {alert.min_stock}
      </p>
      <p className="text-xs text-slate-500">{alert.warehouse_name}</p>
    </div>
  </div>
);

const TransactionItem = ({ transaction }) => (
  <div 
    className="flex items-center justify-between py-3 border-b border-slate-800 last:border-0"
    data-testid={`transaction-${transaction.id}`}
  >
    <div className="flex items-center gap-3">
      <div className={`w-8 h-8 rounded-sm flex items-center justify-center ${
        transaction.transaction_type === "in" ? "bg-emerald-500/10 text-emerald-400" : "bg-red-500/10 text-red-400"
      }`}>
        {transaction.transaction_type === "in" ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
      </div>
      <div>
        <p className="text-sm font-medium text-slate-200">{transaction.product_name}</p>
        <p className="text-xs text-slate-500 font-mono">{transaction.product_sku}</p>
      </div>
    </div>
    <div className="text-right">
      <p className={`text-sm font-mono ${transaction.transaction_type === "in" ? "tx-in" : "tx-out"}`}>
        {transaction.transaction_type === "in" ? "+" : "-"}{transaction.quantity}
      </p>
      <p className="text-xs text-slate-500">${transaction.total.toFixed(2)}</p>
    </div>
  </div>
);

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, alertsRes] = await Promise.all([
        dashboardApi.getStats(),
        dashboardApi.getAlerts(),
      ]);
      setStats(statsRes.data);
      setAlerts(alertsRes.data);
    } catch (error) {
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="animate-fadeIn" data-testid="dashboard">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-2">Dashboard</h1>
        <p className="text-slate-400">Inventory overview and alerts</p>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <MetricCard
          title="Total Products"
          value={stats?.total_products || 0}
          icon={Package}
          color="blue"
        />
        <MetricCard
          title="Total Value"
          value={`$${(stats?.total_value || 0).toLocaleString()}`}
          icon={DollarSign}
          color="green"
        />
        <MetricCard
          title="Low Stock Items"
          value={stats?.low_stock_count || 0}
          icon={AlertTriangle}
          color="red"
        />
        <MetricCard
          title="Warehouses"
          value={stats?.warehouses_count || 0}
          icon={Warehouse}
          color="yellow"
        />
      </div>

      {/* Flow Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        <div className="metric-card">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-5 h-5 text-emerald-400" />
            <span className="text-sm text-slate-400">Recent Inventory In</span>
          </div>
          <p className="text-2xl font-bold text-emerald-400 font-mono">
            ${(stats?.recent_in_value || 0).toLocaleString()}
          </p>
        </div>
        <div className="metric-card">
          <div className="flex items-center gap-2 mb-2">
            <TrendingDown className="w-5 h-5 text-red-400" />
            <span className="text-sm text-slate-400">Recent Inventory Out</span>
          </div>
          <p className="text-2xl font-bold text-red-400 font-mono">
            ${(stats?.recent_out_value || 0).toLocaleString()}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Low Stock Alerts */}
        <div className="bg-[#151923] border border-slate-800 rounded-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-400" />
              Low Stock Alerts
            </h2>
            <Link
              to="/products"
              className="text-sm text-blue-400 hover:text-blue-300 flex items-center gap-1"
              data-testid="view-all-products"
            >
              View All <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          {alerts.length > 0 ? (
            <div className="max-h-80 overflow-y-auto">
              {alerts.slice(0, 5).map((alert) => (
                <AlertItem key={alert.id} alert={alert} />
              ))}
            </div>
          ) : (
            <div className="empty-state py-8">
              <Package className="empty-state-icon" />
              <p className="text-slate-500">No low stock alerts</p>
            </div>
          )}
        </div>

        {/* Recent Transactions */}
        <div className="bg-[#151923] border border-slate-800 rounded-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white">Recent Transactions</h2>
            <Link
              to="/transactions"
              className="text-sm text-blue-400 hover:text-blue-300 flex items-center gap-1"
              data-testid="view-all-transactions"
            >
              View All <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          {stats?.recent_transactions?.length > 0 ? (
            <div className="max-h-80 overflow-y-auto">
              {stats.recent_transactions.map((tx) => (
                <TransactionItem key={tx.id} transaction={tx} />
              ))}
            </div>
          ) : (
            <div className="empty-state py-8">
              <ArrowRight className="empty-state-icon" />
              <p className="text-slate-500">No recent transactions</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
